/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */
import peopleService from "@/services/PeopleService.js";
import { checkString } from "@/directives/assert.js";
import { webscriptApi } from "@/AlfrescoApi.js";
import { useUserStore } from "@/stores/user.js";

/**
 * Represents an abstract element with a unique code and label, optionally associated with a parent.
 */
class AbstractElement {
  /**
   * Vrai si présent dans le plan de classement.
   * Faux indique que c'est un type ou nature hors plan de classement
   * @type {boolean}
   */
  alive = false;

  /**
   * Vrai l'element doit être visible / affiché à l'utilisateur
   * @type {boolean}
   */
  hide = false;

  /**
   * Code du dossier, doit être unique dans le plan de classement
   * @type {string}
   */
  code;

  /**
   * Libellé du dossier, doit être unique dans le plan de classement
   * @type {string}
   */
  label;

  /**
   * Libellé du dossier, parent (optional)
   * @type {string}
   */
  parent;
}

/**
 * Represents a folder type in the filing plan, extending AbstractElement with confidentiality group information.
 */
export class TypeDossier extends AbstractElement {
  /**
   * @type {string[]}
   */
  groupConfidentiel;

  constructor({ code, label, parent, groupConfidentiel, alive = true }) {
    super();
    this.code = code;
    this.label = label;
    this.parent = parent;
    this.groupConfidentiel = groupConfidentiel;
    this.alive = alive;
  }

  isConfidentiel() {
    return this.groupConfidentiel && this.groupConfidentiel.length > 0;
  }
}

/**
 * Represents a document nature in the filing plan, extending AbstractElement with basic properties.
 */
export class NatureDocument extends AbstractElement {
  constructor({ code, label, parent, hide = false }) {
    super();
    this.code = code;
    this.label = label;
    this.parent = parent;
    this.hide = hide;
  }
}

/**
 * This class contains functions need to load and look in the filing plan.
 *
 * Pour chaque fonction on doit fournir l’identifiant du plan de classement
 * car l'utilisateur peut naviguer sur plusieurs espaces en même temps.
 *
 * Pour s'assurer que loadPlanClassement a été appelé au moins une fois, on utilise `waitLoading()`
 */
class FilingPlanService {
  app = "social";
  loading = true;

  constructor() {
    /**
     * Ensemble des filing plans sous format de map "id du plan" => [liste des types de dossiers]
     * @type {Object}
     */
    this.filingPlans = {};
    this.natures = {};
  }

  /**
   * Loads the filing plan for a specific space.
   * The filing plan is kept in memory for 8 hours, as users can navigate through
   * multiple sites with different filing plans, so multiple plans are cached.
   * @param {string} planType - The identifier of the plan to load
   * @returns {Promise} A promise that resolves when the plan is loaded
   */
  async loadPlanClassement(planType) {
    this.loading = true;
    checkString(planType, "planType");
    let filename = `plan-${planType}.json`;
    console.info(`loading plan ${planType}`);
    const url = `fr/jeci/pristy/config/${this.app}/${filename}`;
    return webscriptApi.executeWebScript("GET", url).then((response) => {
      const plan = response["PLAN_CLASSEMENT"];
      const natures = response["NATURE_DOCUMENT"];
      if (!plan) {
        throw new Error(`Missing PLAN_CLASSEMENT in ${filename}`);
      }
      if (!natures) {
        throw new Error(`Missing NATURE_DOCUMENT in ${filename}`);
      }
      this.filingPlans[planType] = plan.map((dossier) => {
        let d = new TypeDossier(dossier);
        d.alive = true;
        return d;
      });

      this.natures[planType] = natures
        .filter((n) => {
          let typeDossier = this.getTypeDossier(planType, n.parent);
          // Remove nature without type in the plan (Probably confidential folders)
          return typeDossier !== null && typeDossier !== undefined;
        })
        .map((nature) => {
          let n = new NatureDocument(nature);
          n.alive = true;
          return n;
        });
      this.loading = false;
    });
  }

  /**
   * Retrieves a folder type from the filing plan by its code.
   * @param {string} planType - The identifier of the filing plan
   * @param {string} code - The unique code of the folder type to retrieve
   * @returns {TypeDossier} An array of matching folder types
   */
  getTypeDossier(planType, code) {
    return this._findPlan(planType, (item) => item.code === code);
  }

  /**
   * Retrieves a folder type from the filing plan by its code.
   * @param {string} planType - The identifier of the filing plan
   * @param {string} label - The unique label of the folder type to retrieve
   * @returns {TypeDossier} An array of matching folder types
   */
  getTypeDossierFromLabel(planType, label) {
    return this._findPlan(planType, (item) => item.label === label);
  }

  _findPlan(planType, filter) {
    this._assertPlanExist(planType);

    return this.filingPlans[planType].find(filter);
  }

  _assertPlanExist(planType) {
    if (!Object.prototype.hasOwnProperty.call(this.filingPlans, planType)) {
      throw new Error(`Filling Plan ${planType} not loaded`);
    }
  }

  /**
   * Retrieves the plan of the specified type, applying confidentiality filters based on user groups.
   *
   * @param {string} planType - The type of the plan to retrieve.
   * @return {TypeDossier[]} A promise resolving to an array of filtered plan items or null if the plan type is not loaded.
   */
  getPlan(planType) {
    if (!Object.prototype.hasOwnProperty.call(this.filingPlans, planType)) {
      console.warn("Filling Plan ${planType} not loaded");
      return null;
    }
    return this.filingPlans[planType];
  }

  /**
   * Retourne une promesse contenant la liste des dossiers filtrés selon les droits de l'utilisateur.
   *
   * Cette méthode vérifie si le plan de classement demandé est chargé.
   * Si oui, elle récupère les groupes de l'utilisateur via le service `peopleService`,
   * puis filtre les dossiers du plan :
   * - Si un dossier est marqué comme confidentiel (`groupConfidentiel`),
   *   il n'est retourné que si l'utilisateur appartient au groupe correspondant.
   * - Les dossiers non confidentiels sont toujours retournés.
   *
   * @param {string} planType - Le type du plan de classement à filtrer.
   * @returns {Promise<Array|Null>} Une promesse qui résout en un tableau de dossiers filtrés,
   *                                ou `null` si le plan de classement n'est pas chargé.
   */
  getFilteredPlan(planType) {
    return peopleService
      .getUserGroups(useUserStore().person.id, false)
      .then((usergroups) => {
        if (!Object.prototype.hasOwnProperty.call(this.filingPlans, planType)) {
          console.warn(`Filing Plan ${planType} not loaded`);
          return null;
        }

        const plan = this.filingPlans[planType];
        return plan.filter((dossier) => {
          if (dossier.groupConfidentiel) {
            return usergroups.includes(dossier.groupConfidentiel);
          }
          return true;
        });
      });
  }

  /**
   * Retrieves a folder type from the filing plan by its code.
   * @param {string} planType - The identifier of the filing plan
   * @param {string} code - The unique code of the folder type to retrieve
   * @returns {NatureDocument} An array of matching folder types
   */
  getNatureDocument(planType, code) {
    if (!Object.prototype.hasOwnProperty.call(this.natures, planType)) {
      console.warn(`Filling Plan ${planType} not loaded`);
      return null;
    }

    return this.natures[planType].find((item) => item.code === code);
  }

  /**
   * Retrieves the nature configuration for the specified plan type.
   *
   * @param {string} planType - The type of plan for which the nature configuration is required.
   * @return {NatureDocument[]} The nature configuration for the provided plan type, or null if not found.
   */
  getNatures(planType) {
    if (!Object.prototype.hasOwnProperty.call(this.natures, planType)) {
      console.warn(`Filling Plan ${planType} not loaded`);
      return null;
    }

    return this.natures[planType];
  }

  /**
   * Retourne les natures activées (non cachées) pour un type de plan donné.
   *
   * @param {string} planType - Le type de plan dont on veut récupérer les natures.
   * @returns {Array<Object>|null} Un tableau des natures où `hide` est false, ou null si le plan n'existe pas.
   */
  getEnabledNatures(planType) {
    if (!Object.prototype.hasOwnProperty.call(this.natures, planType)) {
      console.warn(`Filling Plan ${planType} not loaded`);
      return null;
    }
    return this.natures[planType].filter((nature) => nature.hide === false);
  }

  /**
   * Détermine le TypeDossier de ce folder.
   *
   * Si le code et le nom correspondent au plan de classement, alors alive=true.
   * @param {string} planType - The identifier of the filing plan
   * @param file
   * @return {Promise<TypeDossier>}
   */
  getTypeDossierOfNode(planType, file) {
    const name = this.getFolderNameFromPath(file);
    let type = this.getTypeDossierFromLabel(planType, name);
    if (!type) {
      type = new TypeDossier({ label: `${name} - Hors Plan` });
      type.hide = true;
    }

    return Promise.resolve(type);
  }

  /**
   * Retrieves the folder name from the specified file's path.
   *
   * @param {Object} file - The file object containing the path information.
   * @param {Object} file.path - The path object within the file.
   * @param {Array} file.path.elements - An array of path elements representing the file path.
   * @param {Object} file.path.elements[].name - The name of the folder or file for each path element.
   * @return {string|null} The name of the folder, or null if the path is not provided.
   */
  getFolderNameFromPath(file) {
    if (!file.path) {
      console.warn("No file path provided");
      return null;
    }
    const lastIndexElements = file.path.elements.length - 1;
    return file.path.elements[lastIndexElements].name;
  }

  /**
   * Generates a structured array of tree nodes based on the given plan type.
   *
   *
   * @param {string} planType - The type of plan to be used for generating tree nodes.
   * @return {TreeNode[]} An array of tree node objects, where each node contains properties such as id, key, label, children, and metadata like `isFolder`, `confidentiel`, and icon information.
   */
  async treeNodes(planType) {
    checkString(planType, "planType");

    return this.getFilteredPlan(planType).then((plan) => {
      return plan.map((n) => {
        return {
          id: n.id || null,
          key: n.code,
          label: n.label,
          children: [],
          isFolder: true,
          confidentiel: n.isConfidentiel(),
          icon: "ri-folder-line",
        };
      });
    });
  }
}

export default new FilingPlanService();
